'use client';

import { buildBreadcrumbManager } from '@coveo/atomic-react';
import { analytics } from '@lib/analytics/page-events';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OwnershipFilterProps } from '../OwnershipFilter.types';

type UseOwnershipFilterArgs = Pick<
  OwnershipFilterProps,
  'parentSelector' | 'onToggle' | 'searchEngine'
>;

export const useOwnershipFilter = ({
  parentSelector,
  onToggle,
  searchEngine,
}: UseOwnershipFilterArgs) => {
  const searchParams = useSearchParams();
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Derive state from URL Query Params to coexist with Coveo's Hash handling.
  const checked = searchParams?.get('hideOwnership') === 'true';

  // Breadcrumb Manager to detect "Clear All"
  useEffect(() => {
    if (!searchEngine) return;

    const breadcrumbManager = buildBreadcrumbManager(searchEngine);

    // We track the previous state to detect transitions
    let prevHasBreadcrumbs = breadcrumbManager.state.hasBreadcrumbs;

    const unsubscribe = breadcrumbManager.subscribe(() => {
      const hasBreadcrumbs = breadcrumbManager.state.hasBreadcrumbs;

      // If breadcrumbs clear (e.g. "Clear All" clicked), also clear our custom filter to match user expectation.
      if (prevHasBreadcrumbs && !hasBreadcrumbs) {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.get('hideOwnership')) {
          analytics.pushFilterEvent({
            filter_action: 'deselect',
            filter_action_category: 'ownership',
            filter_action_label: 'hide_owned_assets',
          });
          currentUrl.searchParams.delete('hideOwnership');
          window.history.pushState({}, '', currentUrl);
        }
      }

      prevHasBreadcrumbs = hasBreadcrumbs;
    });

    return () => {
      unsubscribe();
    };
  }, [searchEngine]);

  // Handle DOM side-effect based on derived state
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.querySelector(parentSelector) as HTMLElement | null;
    if (!el) return;
    if (checked) {
      el.setAttribute('data-hide-owned', 'true');
    } else {
      el.removeAttribute('data-hide-owned');
    }
  }, [checked, parentSelector]);

  const handleChange = useCallback(() => {
    if (typeof window === 'undefined') return;
    const currentUrl = new URL(window.location.href);
    const nextState = !checked;

    analytics.pushFilterEvent({
      filter_action: nextState ? 'select' : 'deselect',
      filter_action_category: 'ownership',
      filter_action_label: 'hide_owned_assets',
    });

    if (nextState) {
      currentUrl.searchParams.set('hideOwnership', 'true');
    } else {
      currentUrl.searchParams.delete('hideOwnership');
    }

    if (onToggle) onToggle(nextState);

    // Use pushState to update Query Params without interfering with Coveo's Hash state.
    window.history.pushState({}, '', currentUrl);
  }, [checked, onToggle]);

  const toggleOpen = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return {
    checked,
    open,
    containerRef,
    handleChange,
    toggleOpen,
    close,
  };
};
